import { Badge, InfoTooltip } from '@karnov/kui-react'

import { useDebouncedSearchValue } from 'hooks/useDebouncedSearchValue'
import { useTranslation } from 'services/i18n'
import { isMobile } from 'utils/isMobile'

import type { FC, KeyboardEvent, MouseEvent } from 'react'

type Props = {
  expiresAt?: number
  onClick: (e: KeyboardEvent | MouseEvent) => void
  onDoubleClick?: VoidFunction
  subject: string
}

const getDaysUntilExpiration = (expiresAt?: number) => expiresAt ? Math.ceil(((expiresAt * 1000) - Date.now()) / (1000 * 60 * 60 * 24)) : undefined

export const ConversationSubject: FC<Props> = ({ expiresAt, onClick, onDoubleClick, subject }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'conversation_history',
  })

  const debouncedValue = useDebouncedSearchValue()

  const onKeyDownItem = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') onClick(e)
  }

  const getHighlightedText = () => {
    const escapedSearchValue = debouncedValue.replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&',
    )
    const pattern = new RegExp(`(${escapedSearchValue})`, 'gi')
    const parts = subject.split(pattern)

    return (
      <>
        {parts.map((part, index) => {
          const isMatch = part.toLowerCase() === debouncedValue.toLowerCase()

          const indexKey = `${part}${index}`

          if (isMatch) {
            return (
              <span
                className="bg-karnovnote text-sm font-medium text-default-on-bg-karnovnote"
                key={indexKey}
              >
                {part}
              </span>
            )
          }

          return (
            <span key={indexKey}>
              {index === 0 && part.length > 15
                ? `...${part.substring(part.length - 15, part.length)}`
                : part}
            </span>
          )
        })}
      </>
    )
  }

  if (!subject) return null

  const daysUntilExpiration = getDaysUntilExpiration(expiresAt)

  return (
    <div
      className="relative w-full cursor-pointer overflow-hidden truncate text-sm text-default outline-none"
      onClick={onClick}
      onAuxClick={onClick}
      onKeyDown={onKeyDownItem}
      onDoubleClick={isMobile() ? undefined : onDoubleClick}
      role="menuitem"
      tabIndex={0}
      title={subject}
    >
      {daysUntilExpiration !== undefined && daysUntilExpiration < 30 && (
        <InfoTooltip content={daysUntilExpiration < 1
          ? t('conversation_expiration_expired_tooltip')
          : t('conversation_expiration_tooltip', { count: daysUntilExpiration })}
        >
          <span className="mr-1">
            <Badge isSmall variant="dimmed">
              {daysUntilExpiration < 1 ? t('expired') : t('conversation_expiration', { count: daysUntilExpiration })}
            </Badge>
          </span>
        </InfoTooltip>
      )}
      {!debouncedValue ? subject : getHighlightedText()}
    </div>
  )
}
